import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import { isAfter, parseISO, subDays } from 'date-fns';
import { Alert, Button, Card, Spinner, Table } from 'react-bootstrap';
import { FaFileMedical, FaPrint } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectPatient } from 'redux/features/auth/authSlice';
import useSWR from 'swr';
import { isInfrastructureError } from 'utils/apiErrors';
import api from '../../services/api';
import { ehReceitaLivre } from '../receita/receitaLivre';

async function fetchReceitasPaciente(codPac) {
  const { data } = await api.get(`paciente/${codPac}/receitas`);

  // Filtra receitas emitidas apenas nos últimos 30 dias
  const limite = subDays(new Date(), 30);
  return (data || []).filter((r) => {
    if (ehReceitaLivre(r)) return false;
    if (!r.DATARECEITA) return false;
    const dataReceita = parseISO(r.DATARECEITA);
    return isAfter(dataReceita, limite);
  });
}

export default function ReceitasPaciente() {
  const history = useHistory();
  const patient = useSelector(selectPatient);
  const codPac = patient?.codPac;

  const {
    data: receitas = [],
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR(codPac ? ['receitas-paciente', codPac] : null, ([, id]) => fetchReceitasPaciente(id), {
    revalidateOnFocus: false,
    shouldRetryOnError: true,
    errorRetryCount: 2,
    errorRetryInterval: 3000,
  });

  const temReceitas = receitas.length > 0;
  const exibirErro = Boolean(error) && !isValidating;
  const erroInfraestrutura = isInfrastructureError(error);

  function handleReceita(rec, tipo) {
    history.push({
      pathname: '/imprimir/receita',
      state: {
        receita: {
          ...rec,
          tipo,
          ...(ehReceitaLivre(tipo) ? { TIPORECEITA: 'Livre' } : {}),
        },
      },
    });
  }

  return (
    <Card className="mt-3 shadow-sm border-0">
      <Card.Header as="h5" className="bg-primary text-white py-3 fw-bold">
        Minhas Receitas
      </Card.Header>

      <Card.Body>
        {isLoading && !temReceitas ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" role="status" />
            <p className="mt-2 text-muted small mb-0">Buscando receitas liberadas...</p>
          </div>
        ) : (
          <>
            {exibirErro &&
              (erroInfraestrutura ? (
                <ServiceUnavailableAlert
                  error={error}
                  stale={temReceitas}
                  onRetry={() => mutate()}
                />
              ) : (
                <Alert variant="danger" className="text-center mb-0">
                  Erro ao carregar as receitas. Tente novamente mais tarde.
                  <div className="mt-2">
                    <Button variant="outline-danger" size="sm" onClick={() => mutate()}>
                      Tentar novamente
                    </Button>
                  </div>
                </Alert>
              ))}

            {temReceitas ? (
              <Table responsive striped hover size="sm" className="align-middle mb-0">
                <thead className="table-light border-bottom">
                  <tr>
                    <th className="ps-3 py-2">Código</th>
                    <th className="py-2">Data de Emissão</th>
                    <th className="py-2 text-center">Ações de Impressão</th>
                  </tr>
                </thead>
                <tbody>
                  {receitas.map((rec) => (
                    <tr key={rec.CODRECEITA}>
                      <td className="ps-3 fw-medium">{rec.CODRECEITA}</td>
                      <td>{rec.dataFormatada || rec.DATARECEITA}</td>
                      <td className="text-center py-2">
                        <div className="d-flex justify-content-center gap-2">
                          {ehReceitaLivre(rec) ? (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="d-flex align-items-center gap-1"
                              onClick={() => handleReceita(rec, 'Livre')}
                            >
                              <FaPrint size={14} /> Imprimir
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="d-flex align-items-center gap-1"
                                onClick={() => handleReceita(rec, 'Normal')}
                              >
                                <FaPrint size={14} /> Normal
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="d-flex align-items-center gap-1"
                                onClick={() => handleReceita(rec, 'Controlada')}
                              >
                                <FaFileMedical size={14} /> Controlada
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : !exibirErro ? (
              <Alert variant="warning" className="text-center mb-0">
                Nenhuma receita liberada nos últimos 30 dias.
              </Alert>
            ) : null}
          </>
        )}
      </Card.Body>
    </Card>
  );
}
